import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileUploader } from "./form-utils";
import StepWizard from "./StepWizard";
import { FieldWrap, inputCls, NameFields, PhoneInput, EmailInput, MoneyInput, OptionGrid, Select, Checkbox } from "./fields";
import { PROPERTY_TYPES } from "./MultiStepSellerForm";
import { getTracking } from "@/lib/tracking";
import { submitLead, newSubmissionId, normalizeSubmitError } from "@/lib/submit";
import { emailError, phoneError, formatPhone, moneyValue, moneyDisplay } from "@/lib/format";

const BUYER_TYPES = ["Cash buyer", "Flipper", "Landlord", "BRRRR investor", "Multifamily investor", "Developer", "Agent", "Wholesaler"];
const UNIT_COUNTS = [["1", "1"], ["2-4", "2-4"], ["5+", "5+"]];
const REHAB = ["Light", "Medium", "Heavy", "Full gut"];
const CLOSE = ["7 days", "14 days", "21+ days", "Flexible"];
const CONSENT = "I agree that Brightside Capital Ventures may contact me regarding matching off-market and value-add opportunities.";

const BLANK = { first_name: "", last_name: "", phone: "", email: "", company: "", buyer_type: "", target_areas: "", property_types: [], unit_count_pref: "", price_min: "", price_max: "", rehab_tolerance: "", close_timeline: "", proof_of_funds: [], notes: "", property_updates_opt_in: false, consent: false };

export default function BuyerForm() {
  const navigate = useNavigate();
  const [data, setData] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const submissionId = useRef(newSubmissionId());
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const steps = [
    { id: "contact", label: "About you", title: "How can we reach you?",
      validate: (d) => ({ ...(d.first_name.trim() ? {} : { first_name: "Required" }), ...(d.last_name.trim() ? {} : { last_name: "Required" }), ...(phoneError(d.phone) ? { phone: phoneError(d.phone) } : {}), ...(emailError(d.email) ? { email: emailError(d.email) } : {}) }),
      render: ({ data: d, errors }) => (
        <div className="space-y-5">
          <NameFields first={d.first_name} last={d.last_name} onChange={set} errors={errors} testIdPrefix="buyer" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldWrap label="Phone" error={errors.phone}><PhoneInput value={d.phone} onChange={(v) => set("phone", v)} testId="buyer-phone" /></FieldWrap>
            <FieldWrap label="Email" error={errors.email}><EmailInput value={d.email} onChange={(v) => set("email", v)} testId="buyer-email" /></FieldWrap>
          </div>
          <FieldWrap label="Company" optional><input className={inputCls} value={d.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" data-testid="buyer-company" /></FieldWrap>
        </div>
      ) },
    { id: "buyer_type", label: "Buyer type", title: "What kind of buyer are you?",
      render: ({ data: d }) => <OptionGrid options={BUYER_TYPES} value={d.buyer_type} onChange={(v) => set("buyer_type", v)} testIdPrefix="buyer-type" columns={4} /> },
    { id: "criteria", label: "Buy box", title: "Your buy box",
      render: ({ data: d }) => (
        <div className="space-y-6">
          <FieldWrap label="Target areas"><input className={inputCls} placeholder="Sacramento, Roseville, Rocklin" value={d.target_areas} onChange={(e) => set("target_areas", e.target.value)} data-testid="buyer-target-areas" /></FieldWrap>
          <FieldWrap label="Property types" hint="Select all that apply."><OptionGrid multiple options={PROPERTY_TYPES} value={d.property_types} onChange={(v) => set("property_types", v)} testIdPrefix="buyer-ptype" columns={3} /></FieldWrap>
          <FieldWrap label="Unit count preference"><Select options={UNIT_COUNTS} value={d.unit_count_pref} onChange={(v) => set("unit_count_pref", v)} testId="buyer-unit-count" /></FieldWrap>
        </div>
      ) },
    { id: "budget", label: "Budget", title: "Budget and rehab tolerance",
      validate: (d) => { const lo = moneyValue(d.price_min); const hi = moneyValue(d.price_max); return lo !== null && hi !== null && lo > hi ? { price_max: "Maximum must be at least the minimum" } : {}; },
      render: ({ data: d, errors }) => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldWrap label="Minimum price" optional error={errors.price_min}><MoneyInput value={d.price_min} onChange={(v) => set("price_min", v)} testId="buyer-price-min" placeholder="$250,000" /></FieldWrap>
            <FieldWrap label="Maximum price" optional error={errors.price_max}><MoneyInput value={d.price_max} onChange={(v) => set("price_max", v)} testId="buyer-price-max" placeholder="$750,000" /></FieldWrap>
          </div>
          <FieldWrap label="Rehab tolerance"><OptionGrid options={REHAB} value={d.rehab_tolerance} onChange={(v) => set("rehab_tolerance", v)} testIdPrefix="buyer-rehab" columns={4} /></FieldWrap>
          <FieldWrap label="Close timeline"><OptionGrid options={CLOSE} value={d.close_timeline} onChange={(v) => set("close_timeline", v)} testIdPrefix="buyer-close" columns={4} /></FieldWrap>
        </div>
      ) },
    { id: "extras", label: "Details", title: "Proof of funds and notes", helper: "Both optional.",
      render: ({ data: d }) => (
        <div className="space-y-5">
          <FieldWrap label="Proof of funds" optional><FileUploader kind="buyer" accept="application/pdf,image/*,.heic,.heif" allowPdf value={d.proof_of_funds} onChange={(v) => set("proof_of_funds", v)} testId="buyer-pof" /></FieldWrap>
          <FieldWrap label="Notes" optional><textarea rows={4} className={inputCls} value={d.notes} onChange={(e) => set("notes", e.target.value)} data-testid="buyer-notes" /></FieldWrap>
          <Checkbox checked={d.property_updates_opt_in} onChange={(v) => set("property_updates_opt_in", v)} testId="buyer-updates-opt-in" label="Email me when a property matches my buy box. (Optional. Starts only after you confirm your email address.)" />
        </div>
      ) },
  ];

  const review = (d) => [
    { stepId: "contact", label: "Name", value: `${d.first_name} ${d.last_name}`.trim() },
    { stepId: "contact", label: "Phone", value: formatPhone(d.phone) },
    { stepId: "contact", label: "Email", value: d.email },
    { stepId: "contact", label: "Company", value: d.company },
    { stepId: "buyer_type", label: "Buyer type", value: d.buyer_type },
    { stepId: "criteria", label: "Target areas", value: d.target_areas },
    { stepId: "criteria", label: "Property types", value: d.property_types.join(", ") },
    { stepId: "criteria", label: "Unit count preference", value: d.unit_count_pref },
    { stepId: "budget", label: "Budget", value: (moneyValue(d.price_min) !== null || moneyValue(d.price_max) !== null) ? `${moneyDisplay(moneyValue(d.price_min)) || "No minimum"} to ${moneyDisplay(moneyValue(d.price_max)) || "no maximum"}` : "" },
    { stepId: "budget", label: "Rehab tolerance", value: d.rehab_tolerance },
    { stepId: "budget", label: "Close timeline", value: d.close_timeline },
    { stepId: "extras", label: "Proof of funds", value: d.proof_of_funds[0]?.name },
    { stepId: "extras", label: "Notes", value: d.notes },
    { stepId: "extras", label: "Property updates", value: d.property_updates_opt_in ? "Yes, after email confirmation" : "No" },
  ];

  const submit = async () => {
    setSubmitting(true); setSubmitError(null);
    const tracking = getTracking();
    const payload = { ...data, price_min: moneyValue(data.price_min), price_max: moneyValue(data.price_max), proof_of_funds: data.proof_of_funds[0]?.url || "", ...tracking, source: tracking.utm_source || "direct" };
    try {
      const lead = await submitLead("/api/leads/buyer", payload, submissionId.current);
      submissionId.current = newSubmissionId();
      navigate("/thank-you", { state: { kind: "buyer", lead } });
    } catch (err) {
      const norm = normalizeSubmitError(err);
      setSubmitError(norm);
      toast.error(norm.kind === "validation" ? "Please check the highlighted answers" : "Submission not saved yet");
    } finally { setSubmitting(false); }
  };

  return <StepWizard steps={steps} data={data} setField={set} review={review} consentLabel={CONSENT} onSubmit={submit} submitting={submitting} submitError={submitError} submitLabel="Join Buyer List" testIdPrefix="buyer" />;
}
